/**
 * VH-126: one place — its inventory, and the capture flow that grows it.
 *
 * The location name sits in the header at all times. It is never ambiguous
 * which place you are looking at, which matters the moment a user has more
 * than one gym and the machines have the same names in both.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPinIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import CaptureFlow from '../components/training/CaptureFlow'
import ScanReview from '../components/training/ScanReview'
import EquipmentInventory from '../components/equipment/EquipmentInventory'
import CoveragePanel from '../components/training/CoveragePanel'
import CarryOverPanel from '../components/training/CarryOverPanel'
import {
  useConfirmScan,
  useDiscardScan,
  useTrainingLocation,
  useUpdateLocation,
} from '../hooks/useTrainingLocations'

export default function TrainingLocationDetail() {
  const { id } = useParams()
  const locationId = Number(id)
  const { t } = useTranslation()

  const { data: location, isLoading, error } = useTrainingLocation(locationId)
  const confirmScan = useConfirmScan(locationId)
  const discardScan = useDiscardScan()
  // VH-130: a place named in haste during a scan can be renamed later. The
  // endpoint and this hook both already existed; nothing called them.
  const updateLocation = useUpdateLocation()

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: '', note: '' })
  const [editError, setEditError] = useState(null)

  const startEditing = () => {
    setEditError(null)
    setForm({ name: location?.name || '', note: location?.note || '' })
    setEditing(true)
  }

  const saveEdit = async (event) => {
    event.preventDefault()
    setEditError(null)
    if (!form.name.trim()) {
      setEditError(t('trainingLocations.errors.nameRequired'))
      return
    }
    try {
      await updateLocation.mutateAsync({
        id: locationId,
        data: { name: form.name.trim(), note: form.note },
      })
      setEditing(false)
    } catch (err) {
      // 409 is a name the user already used for another place. Keep their
      // input either way -- retyping a note to fix a name is punishment.
      setEditError(
        err?.response?.data?.detail || t('trainingLocations.errors.saveFailed')
      )
    }
  }

  // The scan currently being reviewed, if any.
  const [scan, setScan] = useState(null)
  const [notice, setNotice] = useState(null)

  const handleQuickResult = (result) => {
    setNotice(null)
    if (!result.recognized) {
      setNotice(t('trainingLocations.scan.nothingFound'))
      setScan({ scan_id: result.scan_id, candidates: [] })
      return
    }
    setScan({ scan_id: result.scan_id, candidates: [result.candidate] })
  }

  const handleScanUpdate = (scanPayload) => {
    setScan({ scan_id: scanPayload.id, candidates: scanPayload.candidates || [] })
  }

  const handleConfirm = async ({ acceptedIds, nameCorrections }) => {
    try {
      const result = await confirmScan.mutateAsync({
        scanId: scan.scan_id,
        acceptedIds,
        nameCorrections,
      })
      setScan(null)
      setNotice(
        t('trainingLocations.scan.saved', {
          created: result.created_equipment_ids.length,
          adopted: result.adopted_equipment_ids.length,
        })
      )
    } catch (err) {
      setNotice(err?.response?.data?.detail || t('trainingLocations.errors.saveFailed'))
    }
  }

  const handleDiscard = async () => {
    // Abandoning a scan must leave no residue and block nothing.
    if (scan?.scan_id) await discardScan.mutateAsync(scan.scan_id)
    setScan(null)
    setNotice(null)
  }

  if (isLoading) return <p className="p-6 text-gray-600">{t('common.loading')}</p>
  if (error || !location) {
    return (
      <p className="p-6 text-red-600" role="alert">
        {t('trainingLocations.errors.loadFailed')}
      </p>
    )
  }

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="location-detail-page">
      <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
        <MapPinIcon className="h-8 w-8 text-primary-600" />
        {location.name}
        <button
          type="button"
          onClick={startEditing}
          data-testid="edit-location-button"
          aria-label={t('trainingLocations.edit.title')}
          className="ml-1 text-gray-400 hover:text-primary-600"
        >
          <PencilSquareIcon className="h-6 w-6" />
        </button>
      </h1>
      <p className="mt-1 text-gray-600">
        {t('trainingLocations.equipmentCount', { count: location.equipment_count })}
      </p>
      {location.note && !editing && (
        <p className="mt-1 text-sm text-gray-500">{location.note}</p>
      )}

      {editing && (
        <form
          onSubmit={saveEdit}
          className="mt-4 space-y-3 rounded-md border border-gray-200 p-4"
          data-testid="edit-location-form"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('trainingLocations.edit.name')} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="edit-location-name"
              maxLength={120}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('trainingLocations.edit.note')}
            </label>
            <textarea
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="edit-location-note"
              rows="2"
              maxLength={2000}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          {editError && (
            <p className="text-sm text-red-600" role="alert" data-testid="edit-location-error">
              {editError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={updateLocation.isPending}
              data-testid="save-location"
            >
              {updateLocation.isPending ? t('common.loading') : t('common.save')}
            </button>
            <button type="button" className="btn-outline" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <section className="mt-6">
        <CaptureFlow
          locationId={locationId}
          onQuickResult={handleQuickResult}
          onScanReady={handleScanUpdate}
        />
      </section>

      {notice && (
        <p className="mt-4 text-sm text-gray-700" data-testid="scan-notice">
          {notice}
        </p>
      )}

      {scan && scan.candidates.length > 0 && (
        <section className="mt-6" data-testid="review-section">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            {t('trainingLocations.scan.reviewTitle')}
          </h2>
          <ScanReview
            candidates={scan.candidates}
            onConfirm={handleConfirm}
            onDiscard={handleDiscard}
            isSaving={confirmScan.isPending}
          />
        </section>
      )}

      {/*
        VH-131: the read-only grid that used to live here is gone. It could show
        Reto a machine and offer no way to fix it — and it was the screen where
        the missing photos were noticed in the first place. Same component as
        /equipment, locked to this place.
      */}
      <section className="mt-8">
        <EquipmentInventory locationId={location.id} locationName={location.name} />
      </section>

      {/* VH-126 Stage C: below the inventory on purpose. You look at what is
          here first, then at what you can do with it. */}
      <CoveragePanel locationId={location.id} />

      {/* VH-126 Stage C2: after coverage, because the gap is the reason to
          carry anything over. Renders nothing when there is no match. */}
      <CarryOverPanel locationId={location.id} />
    </div>
  )
}
